import { ComponentChildren } from 'preact';
import { useRef } from 'preact/hooks';
import { Size } from '#utils/UNSAFE_size';
import { classNames } from '#utils/UNSAFE_classNames';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { ToggleDetail, ListItemRendererContext } from '#UNSAFE_Collection';
import { View } from '#UNSAFE_View';
import { styles } from './themes/TreeViewStyles.css';
import { treeViewVars as cssVars } from './themes/TreeViewContract.css';
import { Expander } from '#PRIVATE_Expander';
import { Flex } from '#UNSAFE_Flex';
import { NodeSkeletonContainer } from './NodeSkeletonContainer';
import { TreeItemSkeletons } from './TreeItemSkeletons';
import { useId } from '#hooks/UNSAFE_useId';
import { useHover } from '#hooks/UNSAFE_useHover';
import { getClientHints } from '#utils/PRIVATE_clientHints';

type Props<K, D> = {
  children: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  itemKey: K;
  depth: number;
  context: ListItemRendererContext<K, D>;
  isExpanded: boolean | undefined;
  onToggle: (detail: ToggleDetail<K>) => void;
  selected: boolean | 'partial';
  isSelectable: boolean;
  isCheckboxless: boolean;
  isTabbable: boolean;
  isLoading?: boolean;
  isFocusRingVisible: boolean;
};

export function TreeItem<K extends string | number, D>({
  itemKey,
  depth,
  context,
  children,
  isExpanded,
  onToggle,
  selected,
  isSelectable,
  isCheckboxless,
  isTabbable,
  isLoading,
  isFocusRingVisible
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const indentation = calcIndentation(depth, isExpanded);
  const skeletonIndentation = calcIndentation(depth + 1, isExpanded);

  const { isHover, hoverProps } = useHover();

  const classes = classNames([
    styles.treeItemStyles.base,
    isFocusRingVisible && styles.treeItemStyles.focused,
    selected === true && styles.treeItemStyles.selected,
    selected === true && isCheckboxless && styles.treeItemStyles.checkboxlessSelected,
    isSelectable && !selected && isHover && styles.treeItemStyles.hovered
  ]);

  return (
    <>
      <div
        ref={rootRef}
        id={useId()}
        tabIndex={isMobile() ? -1 : undefined}
        data-oj-key={itemKey}
        data-oj-tree-item=""
        class={classes}
        role="treeitem"
        aria-level={depth + 1}
        aria-selected={selected ? 'true' : 'false'}
        {...(isExpanded !== undefined && { 'aria-expanded': isExpanded })}
        {...(typeof itemKey === 'number' && { 'data-oj-key-type': 'number' })}
        {...hoverProps}>
        <>
          {selected === true && isHover && <div class={styles.selectedHoveredOverlay}></div>}
          <TabbableModeContext.Provider value={{ isTabbable }}>
            <View paddingInlineStart={indentation as Size}>
              <Flex align="center">
                {isExpanded !== undefined && (
                  <Expander itemKey={itemKey} expanded={isExpanded} onToggle={onToggle} />
                )}
                {children(context)}
              </Flex>
            </View>
          </TabbableModeContext.Provider>
        </>
      </div>
      {isLoading ? (
        <NodeSkeletonContainer indentation={skeletonIndentation as Size}>
          {() => {
            return <TreeItemSkeletons />;
          }}
        </NodeSkeletonContainer>
      ) : null}
    </>
  );
}

export const isMobile = () => {
  const { platform } = getClientHints();
  return platform === 'ios' || platform === 'android';
};

export const calcIndentation = (depth: number, isExpanded?: boolean) => {
  const expanderPlaceholder = isExpanded === undefined ? '40px' : '0px';
  return `calc(${Math.max(0, depth)} * ${cssVars.indentWidth} + ${expanderPlaceholder})`;
};
